import { useRouter } from "next/router";
import { memo, useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { useTranslation } from "next-i18next";

import { fetchAllCountryCityData } from "./http/commonApi";
import axiosInstance from "../utils/axiosInstance";
import { getRecaptchaToken } from "../utils/getcapthcatoken";

const initailObject = {
  name: "",
  email: "",
  phone: "",
};

/**
 * Optimized Maps Embed:
 * - Does NOT add iframe to DOM until:
 *   1) footer is near viewport (IntersectionObserver) OR
 *   2) user clicks "Load map"
 * This prevents Google Maps embed scripts from loading on initial page load.
 */
function FooterMapEmbed() {
  const [showMap, setShowMap] = useState(false);
  const holderRef = useRef(null);

  const mapSrc =
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3609.453494550455!2d55.2811041!3d25.2216463!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f6becb9f676ed%3A0xd45a302f703b789a!2sPrism%20Digital!5e0!3m2!1sen!2sae!4v1753172066266!5m2!1sen!2sae";

  useEffect(() => {
    if (!holderRef.current) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowMap(true);
          io.disconnect();
        }
      },
      { rootMargin: "800px" }, // start loading when user is close to footer
    );

    io.observe(holderRef.current);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={holderRef} style={{ width: 250 }}>
      {!showMap ? (
        <button
          type="button"
          onClick={() => setShowMap(true)}
          aria-label="Load Prism Digital map"
          className="w-[250px] h-[180px] rounded-[10px] overflow-hidden border border-white/20"
          style={{ background: "rgba(255,255,255,0.06)" }}
        >
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-white">
            <span className="text-sm font-medium">Load map</span>
            <span className="text-xs opacity-80">Google Maps</span>
          </div>
        </button>
      ) : (
        <iframe
          title="Prism Digital location map"
          src={mapSrc}
          width="250"
          height="180"
          className="rounded-[10px] overflow-hidden"
          style={{ border: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      )}
    </div>
  );
}

const Footer = memo(({ className = "" }) => {
  const router = useRouter();
  const lang = router.locale || "en";
  const { t } = useTranslation("footer");

  const [formValues, setFormValues] = useState(initailObject);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);

  const [formToken, setFormToken] = useState("");
  const [startedAt] = useState(() => Date.now());
  const [websiteTrap, setWebsiteTrap] = useState(""); // honeypot

  useEffect(() => {
    const loadToken = async () => {
      try {
        const r = await axiosInstance.get(`/form-token?lang=${lang}`);
        setFormToken(r?.data?.formToken || "");
      } catch (e) {
        console.error("Failed to load form token", e);
        setFormToken("");
      }
    };
    loadToken();
  }, [lang]);

  useEffect(() => {
    const fetchCityList = async () => {
      try {
        setCityLoading(true);
        const { data } = await fetchAllCountryCityData(lang);
        setCountries(data?.data || []);
      } catch (error) {
        console.error("Error fetching country list:", error);
      } finally {
        setCityLoading(false);
      }
    };

    fetchCityList();
  }, [lang]);

  const handleInputChange = (e) => {
    setFormValues((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const PostSubscribeFormData = async (updatedData) => {
    const timeOnPageMs = Date.now() - startedAt;

    try {
      const payload = {
        name: updatedData?.name,
        email: updatedData?.email,
        phone: updatedData?.phone,
        formToken,
        timeOnPageMs,
        website: websiteTrap,
        recaptchaToken: updatedData?.recaptchaToken,
      };

      const response = await axiosInstance.post(`/subscription`, payload);

      if (response.status === 200 || response.status === 201) {
        setLoading(false);
        toast.success("You have subscribed successfully.");
        setFormValues({ ...initailObject });
      }
    } catch (error) {
      console.error("Error posting Data:", error);
      setLoading(false);
      toast.error(error?.response?.data?.message || "Something Went wrong!");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formToken) {
      toast.error("Please wait a moment and try again.");
      return;
    }

    const { name, email, phone } = formValues;
    const nextErrors = {};

    if (!name) nextErrors.name = "Please enter your name.";

    if (!email) {
      nextErrors.email = "Please enter your email.";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      nextErrors.email = "Invalid email format.";
    } else if (email.length < 5) {
      nextErrors.email = "Email must be at least 5 characters long.";
    } else if (/\.\./.test(email)) {
      nextErrors.email = "Email cannot contain consecutive dots.";
    } else if (
      /(@example\.com|@tempmail\.com|@disposable\.com)$/i.test(email)
    ) {
      nextErrors.email = "Disposable email addresses are not allowed.";
    }

    if (!phone) nextErrors.phone = "Please enter your contact number.";

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setLoading(true);

    try {
      const recaptchaToken = await getRecaptchaToken("subscription_submit");
      const updatedData = { ...formValues, recaptchaToken };
      await PostSubscribeFormData(updatedData);
    } catch (err) {
      console.error(err);
      toast.error("Verification failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <footer
      className={`
    text-background-color-primary text-sm box-border
    gap-[60px] max-w-full z-[1]
    px-[24px] md:px-[60px] lg:px-[80px] xl:px-[80px]
    py-[40px] md:py-[40px] lg:py-[60px]
    bg-text-primary
    ${className}
  `}
    >
      <div className="relative z-10 w-full flex flex-col md:flex-row flex-wrap items-start justify-start gap-10 max-w-[1600px] mx-auto">
        {/* Logo + Map */}
        <div>
          <Image
            className="h-20 w-20 object-contain cursor-pointer block mb-4"
            loading="lazy"
            width={80}
            height={80}
            alt="prism digital marketing agency logo"
            src="/rectangle-4@2x.png"
            onClick={() => router.push("/")}
          />

          {/* ✅ Optimized: map iframe loads only near footer or on click */}
          <FooterMapEmbed />
        </div>

        {/* Quick Links */}
        <div className={`flex flex-col items-start justify-start`}>
          <h3 className="font-semibold m-0 mb-2">{t("footer.quickLinks")}</h3>
          {[
            { label: "footer.services", path: "/services" },
            { label: "footer.portfolio", path: "/portfolio" },
            { label: "footer.caseStudies", path: "/case-study" },
            { label: "footer.blogs", path: "/blog" },
            { label: "footer.faqs", path: "/faqs" },
          ].map((item) => (
            <Link
              key={item.path}
              href={item.path}
              className="py-2 text-white no-underline font-medium text-[14px] leading-[150%]"
            >
              {t(item.label)}
            </Link>
          ))}

          {lang !== "ar" && (
            <Link
              href="/news"
              className="py-2 text-white no-underline font-medium text-[14px] leading-[150%]"
            >
              {t("footer.news")}
            </Link>
          )}

          {lang !== "ar" && (
            <Link
              href="/podcasts"
              className="py-2 text-white no-underline font-medium text-[14px] leading-[150%]"
            >
              {t("footer.podcasts")}
            </Link>
          )}
        </div>

        {/* Company */}
        <div className={` flex flex-col items-start justify-start`}>
          <h3 className="font-semibold m-0 mb-2">{t("footer.company")}</h3>
          {[
            {
              label: "footer.about",
              path: "/about-prism-digital-marketing-agency",
            },
            { label: "footer.clients", path: "/our-clients" },
            {
              label: "footer.testimonials",
              path: "/digital-marketing-reviews",
            },
            {
              label: "footer.contact",
              path: "/contact-digital-marketing-agency",
            },
            { label: "footer.careers", path: "/careers" },
            { label: "footer.events", path: "/events" },
          ].map((item) => (
            <Link
              key={item.path}
              href={item.path}
              className="py-2 text-white no-underline font-medium text-[14px] leading-[150%]"
            >
              {t(item.label)}
            </Link>
          ))}
        </div>

        {/* Support */}
        <div className={`lg:w-[20%] flex flex-col items-start justify-start`}>
          <h3 className="font-semibold m-0 mb-2">{t("footer.support")}</h3>
          <p className="font-medium text-[14px] leading-[150%] text-white m-0 py-2">
            {t("footer.contactInfo.phone")}{" "}
            <a
              dir="ltr"
              href="tel:043320808"
              className="no-underline text-white hover:text-crimson"
            >
              04 332 0808
            </a>
          </p>

          <p className="font-medium text-[14px] leading-[150%] text-white m-0 py-2">
            {t("footer.contactInfo.email")}{" "}
            <a
              href="mailto:sales@prism-me.com"
              className="no-underline text-white hover:text-crimson"
            >
              sales@prism-me.com
            </a>
          </p>

          <p className="font-medium text-[14px] leading-[150%] text-white m-0 py-2">
            {t("footer.contactInfo.location")}
          </p>
        </div>

        {/* Newsletter */}
        <div className="w-max max-w-full flex flex-1 flex-col items-start justify-start gap-6">
          <div className="flex flex-col items-start justify-start gap-4">
            <h3 className="font-semibold m-0">
              {t("footer.subscribeSection.title")}
            </h3>
            <p className="text-sm font-medium m-0">
              {t("footer.subscribeSection.description")}
            </p>
          </div>
          <div className="w-full flex flex-col gap-4 text-sm">
            <div className="w-full flex flex-col sm:flex-row gap-4">
              <form
                onSubmit={handleSubmit}
                className="w-full box-border flex flex-col gap-4 items-start"
              >
                <div className="m-0 w-full box-border flex flex-col lg:flex-row gap-4">
                  <div className="m-0 w-full box-border">
                    <input
                      type="text"
                      name="name"
                      value={formValues.name}
                      onChange={handleInputChange}
                      className="w-full box-border px-3 py-2 bg-transparent outline-none placeholder-white text-white border border-white rounded-[10px]"
                      placeholder={t(
                        "footer.subscribeSection.inputNamePlaceholder",
                      )}
                    />
                    {errors.name && (
                      <p
                        className="m-0 mt-3 text-[14px]"
                        style={{ color: "red" }}
                      >
                        {errors.name}
                      </p>
                    )}

                    {/* honeypot */}
                    <input
                      type="text"
                      name="website"
                      value={websiteTrap}
                      onChange={(e) => setWebsiteTrap(e.target.value)}
                      autoComplete="off"
                      tabIndex={-1}
                      style={{
                        position: "absolute",
                        left: "-10000px",
                        top: "auto",
                        width: "1px",
                        height: "1px",
                        overflow: "hidden",
                      }}
                    />
                  </div>

                  <div className="m-0 w-full box-border">
                    <input
                      type="email"
                      name="email"
                      value={formValues.email}
                      onChange={handleInputChange}
                      className="w-full box-border px-3 py-2 bg-transparent outline-none placeholder-white text-white border border-white rounded-[10px]"
                      placeholder={t(
                        "footer.subscribeSection.inputEmailPlaceholder",
                      )}
                    />
                    {errors.email && (
                      <p
                        className="m-0 mt-3 text-[14px]"
                        style={{ color: "red" }}
                      >
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                <div className="m-0 w-full box-border">
                  <input
                    type="text"
                    name="phone"
                    value={formValues.phone}
                    onChange={handleInputChange}
                    className="w-full box-border px-3 py-2 bg-transparent outline-none placeholder-white text-white border border-white rounded-[10px]"
                    placeholder={t(
                      "footer.subscribeSection.inputPhonePlaceholder",
                    )}
                  />
                  {errors.phone && (
                    <p
                      className="m-0 mt-3 text-[14px]"
                      style={{ color: "red" }}
                    >
                      {errors.phone}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  className="rounded-31xl bg-background-color-primary text-text-primary py-3 px-6 font-medium cursor-pointer subscribe_form"
                  disabled={loading}
                >
                  {loading
                    ? "Verifying..."
                    : t("footer.subscribeSection.buttonText")}
                </button>
              </form>
            </div>

            <p className="text-sm font-medium text-white m-0">
              {t("footer.subscribeSection.consentText")}
            </p>
          </div>
        </div>

        {/* Country/City Services */}
        {countries?.map((country) => (
          <div
            className="w-full flex flex-col items-start justify-start"
            key={country?.name}
          >
            <h3 className="font-semibold m-0 mb-2">{country?.name}</h3>
            {country?.cities?.map((city, index) => (
              <Link
                key={index}
                href={`/city-services/${city?.slug}`}
                className="py-2 text-white no-underline font-medium text-[14px] leading-[150%]"
              >
                {city?.name}
              </Link>
            ))}
          </div>
        ))}
      </div>

      {/* Bottom Bar */}
      <div className="w-full flex flex-col gap-5 mt-10 max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row pt-5 items-center justify-between gap-5 border-white border-solid border-t-[1px] box-border">
          <div className="text-sm font-medium">{t("footer.copyright")}</div>

          <div className="flex flex-col lg:flex-row gap-3 items-center">
            <Link
              href="/privacy-policy"
              className="font-medium text-[14px] leading-[150%] text-white no-underline"
            >
              {t("footer.privacyPolicy")}
            </Link>

            <div className="flex gap-3 items-center">
              {[
                { url: "https://www.tiktok.com/@prismsocial", icon: "tiktok2" },
                {
                  url: "https://api.whatsapp.com/send?phone=971558500095",
                  icon: "whatsapp2",
                },
                {
                  url: "https://www.facebook.com/PrismSocial/",
                  icon: "facebook2",
                },
                {
                  url: "https://www.instagram.com/prismsocial/",
                  icon: "instagram2",
                },
                { url: "https://x.com/PRISM_Social", icon: "twitter2" },
                {
                  url: "https://www.linkedin.com/company/prismmarketing",
                  icon: "linkedIn2",
                },
                {
                  url: "https://www.youtube.com/@PrismDigitalMarketingAgency",
                  icon: "youtube2",
                },
                {
                  url: "https://www.pinterest.com/prismsocial/",
                  icon: "pinterest2",
                },
              ].map((item) => (
                <a
                  href={item.url}
                  key={item.icon}
                  className="text-white no-underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image
                    src={`/icons/${item.icon}.svg`}
                    alt={item.icon}
                    width={24}
                    height={24}
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.propTypes = {
  className: PropTypes.string,
};

export default Footer;
